import { useContext, useEffect, useId } from "react";

import { CommandRegistryContext } from "./registry";
import type { ResolvedCommand } from "./types";

/**
 * Internal engine behind the JSX command components (Command.tsx). Registers a
 * single command while the component is mounted. Runs every render — the
 * registry diffs metadata and only notifies subscribers when something
 * display-relevant changed, so a fresh command literal each render is fine.
 */
export function useRegisterCommand(command: ResolvedCommand) {
  const registry = useContext(CommandRegistryContext);
  // Stable per call site, so each mounted component is its own registry scope.
  const scope = useId();

  // Intentionally no dependency array: re-register on every render so the
  // registry sees the latest command. It cheaply ignores no-op updates, which
  // is what removes the manual deps array (and its stale-closure footgun).
  useEffect(() => {
    registry.register(scope, command);
  });

  // Separate effect so cleanup runs only on unmount, not on every render.
  useEffect(() => () => registry.unregister(scope), [registry, scope]);
}
